'use client';

import { useState, useCallback } from 'react';
import { Receipt, ArrowLeft, Search, ShoppingCart } from 'lucide-react';
import { ItemSelector } from './components/ItemSelector';
import { ItemCart } from './components/ItemCart';
import { PaymentModal } from './components/PaymentModal';
import { PastOrderList } from './components/PastOrderList';
import type { POSItem, CartItem, Cart, Payment } from './types';
import { calculateCartTotals, generateOrderNo } from './types';

type POSTab = 'items' | 'cart';

interface POSModuleProps {
  onBack?: () => void;
}

export function POSModule({ onBack }: POSModuleProps) {
  // 탭 상태 (모바일용)
  const [activeTab, setActiveTab] = useState<POSTab>('items');

  // 장바구니 상태
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  // 모달 상태
  const [showPayment, setShowPayment] = useState(false);
  const [showPastOrders, setShowPastOrders] = useState(false);

  // 장바구니 계산
  const cartTotals = calculateCartTotals(cartItems);
  const cart: Cart = {
    items: cartItems,
    ...cartTotals,
  };

  // 상품 추가
  const handleAddItem = useCallback((item: POSItem) => {
    setCartItems((prev) => {
      const existingIndex = prev.findIndex((ci) => ci.item.id === item.id);
      if (existingIndex >= 0) {
        // 이미 있으면 수량 증가
        const updated = [...prev];
        const current = updated[existingIndex];
        if (current.quantity < item.stock) {
          updated[existingIndex] = {
            ...current,
            quantity: current.quantity + 1,
          };
        }
        return updated;
      } else {
        // 새로 추가
        return [...prev, { item, quantity: 1, discount: 0 }];
      }
    });
  }, []);

  // 수량 변경
  const handleUpdateQuantity = useCallback((itemId: string, quantity: number) => {
    setCartItems((prev) =>
      prev.map((ci) =>
        ci.item.id === itemId ? { ...ci, quantity } : ci
      )
    );
  }, []);

  // 아이템 삭제
  const handleRemoveItem = useCallback((itemId: string) => {
    setCartItems((prev) => prev.filter((ci) => ci.item.id !== itemId));
  }, []);

  // 장바구니 비우기
  const handleClearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // 결제 완료
  const handlePaymentComplete = useCallback((payments: Payment[]) => {
    // 주문 생성 (실제로는 서버에 저장)
    const order = {
      id: `order-${Date.now()}`,
      orderNo: generateOrderNo(),
      items: cartItems,
      ...cartTotals,
      payments,
      paidAmount: payments.reduce((sum, p) => sum + p.amount, 0),
      change: payments.reduce((sum, p) => sum + p.amount, 0) - cartTotals.total,
      status: 'paid' as const,
      createdAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
    };

    console.log('주문 완료:', order);

    // 장바구니 비우기
    setCartItems([]);
    setShowPayment(false);
  }, [cartItems, cartTotals]);

  return (
    <div className="flex flex-col h-full -m-6">
      {/* 상단 툴바 */}
      <div className="flex items-center justify-between px-4 py-3 bg-card border-b border-border">
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-accent"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
          )}
          <h1 className="text-xl font-bold">POS</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowPastOrders(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-accent"
          >
            <Receipt className="w-4 h-4" />
            <span className="hidden sm:inline">주문 내역</span>
          </button>
        </div>
      </div>

      {/* 탭 헤더 */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('items')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
            activeTab === 'items'
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
          }`}
        >
          <Search className="w-4 h-4" />
          상품 검색
        </button>
        <button
          onClick={() => setActiveTab('cart')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all ${
            activeTab === 'cart'
              ? 'border-primary text-primary bg-primary/5'
              : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent'
          }`}
        >
          <ShoppingCart className="w-4 h-4" />
          장바구니
          {cartItems.length > 0 && (
            <span className={`px-1.5 py-0.5 text-xs rounded ${
              activeTab === 'cart'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground'
            }`}>
              {cartItems.length}
            </span>
          )}
        </button>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="flex-1 overflow-hidden">
        {/* 상품 선택기 */}
        <div className={`h-full ${activeTab !== 'items' ? 'hidden' : ''}`}>
          <ItemSelector onSelectItem={handleAddItem} />
        </div>

        {/* 장바구니 */}
        <div className={`h-full ${activeTab !== 'cart' ? 'hidden' : ''}`}>
          <ItemCart
            cart={cart}
            onUpdateQuantity={handleUpdateQuantity}
            onRemoveItem={handleRemoveItem}
            onClearCart={handleClearCart}
            onCheckout={() => setShowPayment(true)}
          />
        </div>
      </div>

      {/* 결제 모달 */}
      {showPayment && cartItems.length > 0 && (
        <PaymentModal
          cart={cart}
          onClose={() => setShowPayment(false)}
          onComplete={handlePaymentComplete}
        />
      )}

      {/* 주문 내역 패널 */}
      {showPastOrders && (
        <PastOrderList onClose={() => setShowPastOrders(false)} />
      )}
    </div>
  );
}
